import './FAQPage.scss'

import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { head, isNil } from 'lodash'

import Accordion from '../../components/Accordion'
import DropDown from '../../components/DropDown'
import Loading from '../../components/Loading'
import LinkWithLocale from '../../components/LinkWithLocale'

import { useFaqQuery, Language } from '../../types/generated'

const languageMap: { [key: string]: Language } = {
  en: Language.EN,
  'zh-hans': Language.ZH_HANS,
  'zh-hant': Language.ZH_HANT,
}

export default function FAQPage() {
  const {
    i18n: { language },
    t,
  } = useTranslation()
  const [isActiveDropdown, setIsActiveDropdown] = useState(false)
  const [currentTabId, setCurrentTabId] = useState<number>(0)

  const { data, loading, called, refetch } = useFaqQuery({
    variables: {
      locale: languageMap[language],
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (called) {
      refetch({ locale: languageMap[language] })
    }
  }, [language, called, refetch])

  useEffect(() => {
    if (isNil(data)) {
      return
    }

    if (!currentTabId) {
      setCurrentTabId(head(data.faq)?.id ?? 0)
    }
    // eslint-disable-next-line
  }, [data])

  const tabs = data?.faq.map(tab => ({ id: tab.id, name: tab.name })) ?? []
  const selectedArticles = data?.faq.find(tab => tab.id === currentTabId)?.articles ?? []

  return (
    <div className="FAQPage">
      <h1 className="FAQPage__title">FAQ</h1>
      <div className="FAQPage__subtitle">{t('faq.subtitle')}</div>
      {data?.faq && (
        <>
          <div className="FAQPage__content">
            <div className="FAQPage__form">
              <div className="FAQPage__tabWrapper">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    className={classNames('FAQPage__tab', {
                      'FAQPage__tab--selected': currentTabId === tab.id,
                    })}
                    onClick={() => {
                      setCurrentTabId(tab.id)
                    }}
                  >
                    {tab.name}
                  </button>
                ))}
                <DropDown
                  className="FAQPage__dropdownWrapper"
                  buttonId="FAQTabs"
                  title={tabs.find(tab => tab.id === currentTabId)?.name ?? ''}
                  items={tabs.map(({ name, id }) => ({
                    label: name,
                    value: id,
                  }))}
                  onClick={(activeState: boolean) => setIsActiveDropdown(activeState)}
                  onSelect={(id: string) => {
                    setIsActiveDropdown(false)
                    setCurrentTabId(parseInt(id, 10)) // FIXME: 에러유발 가능성이 있는 코드. number string이 아닌경우 parseInt하면 NaN.
                  }}
                />
              </div>
              <div
                className={classNames('FAQPage__articleWrapper', {
                  'FAQPage__articleWrapper--dim': isActiveDropdown,
                })}
              >
                {selectedArticles.map(article => (
                  <Accordion key={article.id} title={article.title} contents={article.description} />
                ))}
              </div>
            </div>
          </div>
          <div className="FAQPage__contact">
            {t('faq.contact1')}&nbsp;
            <LinkWithLocale to="/contact-us" className="FAQPage__contact__link">
              {t('faq.contact2')}
            </LinkWithLocale>
          </div>
        </>
      )}
      <Loading className="FAQPage__loading" visible={loading} size={40} />
    </div>
  )
}
